import React, { useEffect, useRef, useState, useCallback } from "react";
import { Wheel } from "react-custom-roulette";
import cogoToast from "cogo-toast";
import { fetchPromotions, addReward } from "../api/dealsApi";
import "../assets/scss/spinwheel.scss";

const SHOW_AFTER_MS = 30000;
const SPIN_COOLDOWN_HOURS = 24;
const COOLDOWN_MS = SPIN_COOLDOWN_HOURS * 60 * 60 * 1000;

const NO_LUCK_SLICE = {
  option: "NO LUCK",
  isNoLuck: true,
  style: {
    backgroundColor: "#ECEFF6",
    textColor: "#555",
  },
};

export default function SpinWheelPopup() {
  const spinSound = useRef(null);
  const winSound = useRef(null);

  const [visible, setVisible] = useState(false);
  const [wheelData, setWheelData] = useState([]);
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeIndex, setPrizeIndex] = useState(0);
  const [spinLocked, setSpinLocked] = useState(false);
  const popupTimerRef = useRef(null);

  const showToast = (msg, type = "success") =>
    cogoToast[type](msg, { position: "bottom-left" });

  useEffect(() => {
    spinSound.current = new Audio("/sounds/spin.wav");
    spinSound.current.loop = true;
    spinSound.current.volume = 0.4;

    winSound.current = new Audio("/sounds/win.wav");
    winSound.current.volume = 0.8;
  }, []);

  const canShowSpinPopup = () => {
    const lastSeen = localStorage.getItem("SPIN_POPUP_SEEN_AT");
    if (!lastSeen) return true;

    const diff = Date.now() - Number(lastSeen);
    return diff >= COOLDOWN_MS;
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      setVisible(false);
      return;
    }

    if (!canShowSpinPopup()) {
      setVisible(false);
      return;
    }

    popupTimerRef.current = setTimeout(() => {
      const latestToken = localStorage.getItem("authToken");

      if (latestToken || !canShowSpinPopup()) return;

      setVisible(true);
      localStorage.setItem("SPIN_POPUP_SEEN_AT", Date.now().toString());
    }, SHOW_AFTER_MS);

    return () => {
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
        popupTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      setVisible(false);

      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
        popupTimerRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (!visible) return;

    const loadSpinPromotions = async () => {
      const promotions = await fetchPromotions();

      const activeSpinPromos = promotions.filter(
        (p) => p.status === "active" && p.enable_spin_picker === 1
      );

      if (activeSpinPromos.length < 5) {
        setVisible(false);
        return;
      }

      const promoSlices = activeSpinPromos.map((p, i) => ({
        option: p.code,
        promotion: {
          promotion_id: p.promotion_id,
          code: p.code,
          description: p.campaign?.description || "",
          discounted_value: p.applicationMethods?.[0]?.value || "",
          minimum_order_value: p.rules?.[0]?.ruleValues?.[0]?.value || "0",
          expired_date: p.campaign?.ends_at,
        },
        style: {
          backgroundColor: i % 2 === 0 ? "#E8F0FF" : "#D6E2FF",
          textColor: "#0F1A2A",
        },
      }));

      setWheelData([...promoSlices, NO_LUCK_SLICE]);
    };

    loadSpinPromotions();
  }, [visible]);

  const onSpinStart = () => {
    if (spinLocked) return;

    spinSound.current.currentTime = 0;
    spinSound.current.play();

    setSpinLocked(true);
    setPrizeIndex(Math.floor(Math.random() * wheelData.length));
    setMustSpin(true);
  };

  const onSpinStop = useCallback(async () => {
    localStorage.setItem("SPIN_POPUP_SEEN_AT", Date.now().toString());
    spinSound.current.pause();
    spinSound.current.currentTime = 0;
    winSound.current.play();

    const picked = wheelData[prizeIndex];

    setMustSpin(false);
    setSpinLocked(false);
    setVisible(false);

    if (picked.isNoLuck) {
      showToast("Better luck next time!", "info");
      setTimeout(() => {
        window.location.href = "/login-register";
      }, 600);
      return;
    }

    localStorage.setItem("pendingPromotion", JSON.stringify(picked.promotion));

    showToast("Login to save your reward", "info");
    setTimeout(() => {
      window.location.href = "/login-register";
    }, 600);
  }, [prizeIndex, wheelData]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const pending = localStorage.getItem("pendingPromotion");

    if (!token || !pending) return;

    const promotion = JSON.parse(pending);
    localStorage.removeItem("pendingPromotion");

    addReward(promotion).then((res) => {
      if (res.success) {
        showToast("Coupon saved in rewards", "success");
      } else {
        showToast(
          res?.error?.message || "You have already claimed this coupon",
          "info"
        );
      }

      setTimeout(() => {
        window.location.href = "/shop-grid-standard";
      }, 1200);
    });
  }, []);

  if (!visible || wheelData.length === 0) return null;

  return (
    <div className="csw-overlay">
      <div className="csw-wheel-area clean">
        <button
          className="csw-close"
          onClick={() => {
            localStorage.setItem("SPIN_POPUP_SEEN_AT", Date.now().toString());
            setVisible(false);
          }}
        >
          ✕
        </button>

        {!mustSpin && (
          <div className="csw-center-spin" onClick={onSpinStart}>
            SPIN
          </div>
        )}

        <Wheel
          mustStartSpinning={mustSpin}
          prizeNumber={prizeIndex}
          data={wheelData}
          onStopSpinning={onSpinStop}
          outerBorderWidth={1}
          outerBorderColor="#D9E8FF"
          radiusLineWidth={0}
          fontSize={16}
        />
      </div>
    </div>
  );
}
